import { ItemSheetData } from "@/types/itemsheet";
import { BidSummary, BuyerGroupingTable, Scenario } from "@/types/scenario";

export function calculateGroupSummary(
  items: ItemSheetData[],
  discountRate: number
) {
  let totalListPrice = 0;
  let totalCost = 0;

  for (const item of items) {
    const quantity = item.quantity ?? 0;
    const listPrice = item.list_price ?? 0;
    totalListPrice += quantity * listPrice;
    totalCost += item.total_cost ?? 0;
  }

  const supplyPrice = totalListPrice * (1 - discountRate / 100);

  const margin = supplyPrice - totalCost;
  const marginRate = supplyPrice > 0 ? (margin / supplyPrice) * 100 : 0;

  return {
    totalListPrice: Math.round(totalListPrice),
    discountRate,
    supplyPrice: Math.round(supplyPrice),
    totalCost: Math.round(totalCost),
    margin: Math.round(margin),
    marginRate,
  };
}

export function convertScenarioToGroupingTable(
  data: Scenario["data"]
): BuyerGroupingTable[] {
  const groups = Object.entries(data).map(([buyer, { discountRate, items = [] }]) => {
    return {
      buyer,
      items,
      discountRate,
      summary: calculateGroupSummary(items, discountRate),
    };
  });

  // Custom Price 아이템들을 별도 그룹으로 분리
  const result: BuyerGroupingTable[] = [];
  groups.forEach((group) => {
    const regularItems = group.items.filter((item: ItemSheetData) => !(item as any).isCustomPrice);
    const customPriceItems = group.items.filter((item: ItemSheetData) => (item as any).isCustomPrice);

    // 일반 아이템들로 기존 그룹 유지
    if (regularItems.length > 0) {
      result.push({
        ...group,
        items: regularItems,
        summary: calculateGroupSummary(regularItems, group.discountRate),
      });
    }

    // Custom Price 아이템들로 새로운 그룹 생성
    if (customPriceItems.length > 0) {
      result.push({
        buyer: `${group.buyer} C`,
        items: customPriceItems,
        discountRate: group.discountRate,
        summary: calculateGroupSummary(customPriceItems, group.discountRate),
        isCustomGroup: true,
      });
    }
  });

  return result;
}

export function calculateScenarioBidSummary({
  scenarioData,
  budgetAmountMap,
}: {
  scenarioData: Scenario["data"];
  budgetAmountMap: { budget: number; basisAmount: string | undefined };
}): BidSummary {
  const groupList = convertScenarioToGroupingTable(scenarioData);

  const bidAmount =
    groupList.reduce((acc, { summary }) => acc + summary.supplyPrice, 0) * 1.1;

  const baseAmount = budgetAmountMap.basisAmount
    ? Number(budgetAmountMap.basisAmount)
    : budgetAmountMap.budget;
  const bidRate = baseAmount > 0 ? (bidAmount / baseAmount) * 100 : 0;

  return {
    groupList,
    bidAmount: Math.round(bidAmount),
    baseAmountMap: budgetAmountMap,
    bidRate,
  };
}

export const formatScenarioRequestData = (divisions: BuyerGroupingTable[]) => {
  console.log("formatScenarioRequestData - input divisions:", divisions);
  const result = divisions.reduce(
    (acc, curr) => {
      acc[curr.buyer] = {
        discountRate: curr.discountRate,
        items: curr.items,
      };
      return acc;
    },
    {} as Record<
      string,
      {
        discountRate: number;
        items: ItemSheetData[];
      }
    >
  );
  console.log("formatScenarioRequestData - result:", result);
  return result;
};

export const getNextScenarioTitle = (scenarios: Scenario[]) => {
  const nums = scenarios
    .map(({ title }) => {
      const match = title.match(/^시나리오 (\d+)$/);
      return match ? parseInt(match[1], 10) : null;
    })
    .filter((num): num is number => num !== null);

  const maxNum = nums.length > 0 ? Math.max(...nums) : 0;
  return `시나리오 ${maxNum + 1}`;
};
